import type { AdminConsoleGroup, AdminPermissionCatalogItem } from '../api/adminConsole';

export type AdminGroupSortKey = 'name' | 'members' | 'permissions';
export type AdminGroupSortDir = 'asc' | 'desc';
export type PermissionGroupSelection = 'all' | 'some' | 'none';

export function groupCatalogBySection(
  items: readonly AdminPermissionCatalogItem[],
): Array<[string, AdminPermissionCatalogItem[]]> {
  const map = new Map<string, AdminPermissionCatalogItem[]>();
  for (const item of items) {
    const list = map.get(item.group) ?? [];
    list.push(item);
    map.set(item.group, list);
  }
  return [...map.entries()];
}

export function permissionGroupSelection(
  selected: readonly string[],
  keys: readonly string[],
): PermissionGroupSelection {
  if (keys.length === 0) return 'none';
  const hit = keys.filter((key) => selected.includes(key)).length;
  if (hit === 0) return 'none';
  if (hit === keys.length) return 'all';
  return 'some';
}

export function applyPermissionGroupToggle(
  selected: readonly string[],
  keys: readonly string[],
  checked: boolean,
): string[] {
  if (checked) return [...new Set([...selected, ...keys])];
  const drop = new Set(keys);
  return selected.filter((key) => !drop.has(key));
}

export function filterAssignablePermissions(
  selected: readonly string[],
  catalog: readonly AdminPermissionCatalogItem[],
): string[] {
  const allowed = new Set(catalog.map((item) => item.key));
  return selected.filter((key) => allowed.has(key));
}

export function nextAdminGroupSort(
  currentKey: AdminGroupSortKey,
  currentDir: AdminGroupSortDir,
  clicked: AdminGroupSortKey,
): { key: AdminGroupSortKey; dir: AdminGroupSortDir } {
  if (currentKey === clicked) {
    return { key: clicked, dir: currentDir === 'asc' ? 'desc' : 'asc' };
  }
  return { key: clicked, dir: 'asc' };
}

function sortValue(group: AdminConsoleGroup, key: AdminGroupSortKey): string | number {
  switch (key) {
    case 'name':
      return group.name.toLowerCase();
    case 'members':
      return group.memberCount ?? 0;
    case 'permissions':
      return group.permissions.length;
    default:
      return '';
  }
}

export function compareAdminGroups(
  a: AdminConsoleGroup,
  b: AdminConsoleGroup,
  key: AdminGroupSortKey,
  dir: AdminGroupSortDir,
): number {
  const left = sortValue(a, key);
  const right = sortValue(b, key);
  const cmp =
    typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
}

export function sortAdminGroups(
  items: readonly AdminConsoleGroup[],
  key: AdminGroupSortKey,
  dir: AdminGroupSortDir,
): AdminConsoleGroup[] {
  return [...items].sort((a, b) => compareAdminGroups(a, b, key, dir));
}
